import { cardsService } from '@/services'
import { useCardsStore } from '@/store/cards/cards'
import { getLocalStorage, setLocalStorage } from '@/shared/helpers/localStorage'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useInterval } from 'usehooks-ts'
import { useShallow } from 'zustand/react/shallow'
import { useQuery } from '@tanstack/react-query'
import { usePathname } from 'next/navigation'
import { createCanIssueCardsQueryOptions, createCardsQueryOptions, createTariffsQueryOptions } from './queries'
import { createUserProfileQueryOptions } from '@/features/User/queries'
import { computeCardsRoute } from './cards.helper'

export function useCardsRoute() {
  const pathname = usePathname() ?? ''
  const { data: profile, isPending: profilePending } = useQuery(createUserProfileQueryOptions())
  const { data: canIssueData, isPending: canIssuePending } = useQuery(createCanIssueCardsQueryOptions())
  const { data: cardsData } = useQuery(createCardsQueryOptions())

  const hasExistingCards = (cardsData?.length ?? 0) > 0

  // Backfill the local flag once we know the user already has a card, so other places that
  // read it directly (KycProvider, CardWelcome) see a consistent value on their next read too.
  useEffect(() => {
    if (hasExistingCards && getLocalStorage('godefi:card:rules:accepted') !== 'true') {
      setLocalStorage('godefi:card:rules:accepted', 'true')
    }
  }, [hasExistingCards])

  const route = useMemo(
    () => computeCardsRoute(
      profile ?? null,
      canIssueData?.canIssue ?? false,
      canIssueData?.reason ?? [],
      pathname,
      hasExistingCards,
    ),
    [profile, canIssueData?.canIssue, canIssueData?.reason, pathname, hasExistingCards],
  )

  return {
    route,
    isPending: profilePending || canIssuePending,
  }
}

export function useCards(withFetch: boolean = true, withRefetch: boolean = false) {
  const { data, isLoading, error, isSuccess, refetch } = useQuery(createCardsQueryOptions(withFetch))

  useEffect(() => {
    if (withFetch && withRefetch) {
      const interval = setInterval(() => refetch(), 5000)
      return () => clearInterval(interval)
    }
  }, [withFetch, withRefetch, refetch])

  const call = useCallback(async () => {
    const result = await refetch()
    return result.data
  }, [refetch])

  return {
    isLoading,
    error: error ?? null,
    data: data ?? null,
    call,
    isSuccess: isSuccess ?? false,
  }
}

export function useCanIssueCards(withFetch: boolean = true, withRefetch: boolean = false) {
  const { setCanIssueCard, setCanIssueCardReason } = useCardsStore(useShallow((state) => ({
    setCanIssueCard: state.setCanIssueCard,
    setCanIssueCardReason: state.setCanIssueCardReason,
  })))

  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [data, setData] = useState(null)
  const [isSuccess, setIsSuccess] = useState(false)

  const call = useCallback(async () => {
    if (!data) {
      setIsLoading(true)
    }

    try {
      const { canIssue, payload } = await cardsService.canIssue()
      setData(canIssue)
      setIsSuccess(true)
      setCanIssueCard(canIssue)
      if (payload) setCanIssueCardReason(Object.keys(payload))
    } catch (e) {
      setError(e)
      setIsSuccess(false)
    }

    setIsLoading(false)
  }, [data, setCanIssueCard, setCanIssueCardReason])

  useInterval(async () => {
    if (withFetch) {
      await call()
    }
  }, withRefetch ? 5000 : null)

  useEffect(() => {
    if (withFetch) {
      call().then(() => {})
    }
  }, [withFetch])

  return { isLoading, error, data, call, isSuccess }
}

export function useTariffs(withFetch: boolean = true, withRefetch: boolean = false) {
  const { data, isLoading, error, isSuccess, refetch } = useQuery(
    createTariffsQueryOptions(withFetch)
  )

  // Handle refetch interval if needed
  useEffect(() => {
    if (withFetch && withRefetch) {
      const interval = setInterval(() => {
        refetch()
      }, 5000)
      return () => clearInterval(interval)
    }
  }, [withFetch, withRefetch, refetch])

  const call = useCallback(async () => {
    const result = await refetch()
    return result.data
  }, [refetch])

  return {
    isLoading,
    call,
    error,
    data: data || null,
    isSuccess: isSuccess || false,
  }
}
